import React, { useState } from "react";
import {
  Box,
  Typography,
  Fade,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  IconButton,
  Chip,
  Pagination,
  Skeleton,
  Collapse,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ContentCopy, Print, VpnKey, Edit, FilterList, FilterListOff } from "@mui/icons-material";
import { Search, Filter, Trash2, Server } from "lucide-react";
import { ColumnVisibilityButton } from "../../shared/components/ColumnVisibilityButton";
import { SortableHeader } from "../../shared/components/SortableHeader";
import { MobileSortControl } from "../../shared/components/MobileSortControl";
import { PageHeader } from "../../shared/components/PageHeader";
import { EmptyState } from "../../shared/components/EmptyState";
import { formatToIST } from "../../utils/formatting";
import { searchOptions, isRecentlyRegistered } from "../../utils/licenseStatus";
import { getStatusColorSx } from "../../theme/theme";

function SystemsView({
  darkMode,
  theme,
  searchTerm,
  handleSearch,
  searchType,
  setSearchType,
  sortConfig,
  handleSort,
  visibleColumns,
  setVisibleColumns,
  toggleColumn,
  columnMenuAnchor,
  activeColumnTable,
  handleColumnMenuOpen,
  handleColumnMenuClose,
  page,
  handlePageChange,
  rowsPerPage,
  isMobile,
  loading,
  filteredSystems,
  paginatedSystems,
  copiedField,
  handleCopy,
  handleSystemHover,
  handleSystemHoverClose,
  handleAddSystem,
  handleEditSystem,
  handlePrint,
  deleteSystem,
  fetchSystemOptions,
  setNewLicense,
  setSystemSearch,
  setOpenDialog,
}) {
  const [showFilters, setShowFilters] = useState(true);

  return (
    <Fade in={true} timeout={500}>
      <Box key="systems-view">
        <PageHeader
          title="System Management"
          subtitle="Monitor and configure your systems"
          darkMode={darkMode}
          actions={
            <>
              <Tooltip title={showFilters ? "Hide Filters" : "Show Filters"}>
                <Button
                  variant="outlined"
                  size="small"
                  color={showFilters ? "primary" : "inherit"}
                  startIcon={showFilters ? <FilterListOff fontSize="small" /> : <FilterList fontSize="small" />}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  Filters
                </Button>
              </Tooltip>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Print />}
                onClick={() => handlePrint('systems')}
              >
                Print
              </Button>
              <Button
                variant="contained"
                startIcon={<Server size={18} />}
                onClick={handleAddSystem}
                sx={{ px: 4, py: 1.5 }}
              >
                Add New System
              </Button>
              <ColumnVisibilityButton
                tableType="systems"
                visibleColumns={visibleColumns}
                setVisibleColumns={setVisibleColumns}
                toggleColumn={toggleColumn}
                columnMenuAnchor={columnMenuAnchor}
                activeColumnTable={activeColumnTable}
                handleColumnMenuOpen={handleColumnMenuOpen}
                handleColumnMenuClose={handleColumnMenuClose}
                theme={theme}
              />
            </>
          }
        />

        <Collapse in={showFilters}>
          <Card sx={{ mb: 3, bgcolor: darkMode ? '#0E1626' : 'background.paper' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  <Filter size={20} color={darkMode ? '#93A2BC' : theme.palette.text.secondary} />
                  <Typography variant="body2" sx={{ color: darkMode ? '#93A2BC' : 'text.secondary', fontWeight: 500 }}>
                    Filter by:
                  </Typography>
                </Box>
                <TextField
                  variant="outlined"
                  placeholder={`Search by ${searchOptions.systems.find(o => o.value === searchType)?.label || 'System UID'}`}
                  value={searchTerm}
                  onChange={handleSearch}
                  size="small"
                  sx={{ minWidth: 300 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={18} />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Search By</InputLabel>
                  <Select
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value)}
                    label="Search By"
                  >
                    {searchOptions.systems.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </CardContent>
          </Card>
        </Collapse>

        {loading.systems ? (
          <Card sx={{ bgcolor: darkMode ? '#0E1626' : 'background.paper' }}>
            {isMobile ? (
              <CardContent>
                {[...Array(4)].map((_, i) => <Skeleton key={i} variant="rounded" height={80} sx={{ mb: 1.5 }} />)}
              </CardContent>
            ) : (
              <TableContainer>
                <Table sx={{ minWidth: 1000 }}>
                  <TableBody>
                    {[...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        {[...Array(6)].map((__, j) => (
                          <TableCell key={j}><Skeleton variant="text" /></TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        ) : isMobile ? (
          <Card sx={{ bgcolor: darkMode ? '#0E1626' : 'background.paper' }}>
            <CardContent sx={{ p: 2 }}>
              <MobileSortControl
                tableType="systems"
                currentConfig={sortConfig.systems}
                handleSort={handleSort}
                options={[
                  { value: 'uuid', label: 'System UID' },
                  { value: 'project_id', label: 'Project ID' },
                  { value: 'software_id', label: 'Software ID' },
                  { value: 'amc', label: 'AMC Status' },
                  { value: 'registered_at', label: 'Registered At' },
                ]}
              />
              {paginatedSystems.map((system) => (
                <Box
                  key={system.uuid}
                  sx={{ p: 2, mb: 1.5, borderRadius: 2, bgcolor: darkMode ? '#16213A' : '#EAF3FB', border: darkMode ? '1px solid #24304A' : '1px solid #D8E1E8' }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace', color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                      {system.uuid.substring(0, 16)}...
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {isRecentlyRegistered(system.registered_at) && (
                        <Chip size="small" label="New" sx={getStatusColorSx('new', darkMode)} />
                      )}
                      <Chip size="small" label={system.amc ? "AMC" : "No AMC"} sx={{
                        bgcolor: system.amc ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.grey[500], 0.1),
                        color: system.amc ? 'success.main' : (darkMode ? '#93A2BC' : 'text.secondary'),
                        fontWeight: 600,
                      }} />
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <Chip size="small" label={system.project_id || 'N/A'} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', fontWeight: 600 }} />
                    <Chip size="small" label={system.software_id || 'N/A'} sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1), color: 'secondary.main', fontWeight: 600 }} />
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', color: darkMode ? '#93A2BC' : 'text.secondary', mb: 1.5 }}>
                    Registered {formatToIST(system.registered_at)}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                    <IconButton
                      color="success"
                      size="small"
                      onClick={async () => {
                        const systemOptions = await fetchSystemOptions();
                        setNewLicense({ uuid: system.uuid, start_date: '', end_date: '', systemOptions });
                        setSystemSearch(system.uuid);
                        setOpenDialog(prev => ({ ...prev, license: true }));
                      }}
                    >
                      <VpnKey fontSize="small" />
                    </IconButton>
                    <IconButton color="primary" onClick={() => handleEditSystem(system)} size="small">
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton color="error" onClick={() => deleteSystem(system.uuid)} size="small">
                      <Trash2 size={18} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
              {filteredSystems.length === 0 && (
                <EmptyState darkMode={darkMode} title="No systems found" subtitle="Try adjusting your search or add a new system" />
              )}
              {filteredSystems.length > rowsPerPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                  <Pagination count={Math.ceil(filteredSystems.length / rowsPerPage)} page={page} onChange={handlePageChange} color="primary" size="medium" />
                </Box>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card sx={{ bgcolor: darkMode ? '#0E1626' : 'background.paper' }}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader sx={{ minWidth: 1000 }}>
                <TableHead>
                  <TableRow>
                    {visibleColumns.systems.uuid && (
                      <SortableHeader label="System UID" tableType="systems" columnKey="uuid" currentConfig={sortConfig.systems} handleSort={handleSort} />
                    )}
                    {visibleColumns.systems.project_id && (
                      <SortableHeader label="Project ID" tableType="systems" columnKey="project_id" currentConfig={sortConfig.systems} handleSort={handleSort} />
                    )}
                    {visibleColumns.systems.software_id && (
                      <SortableHeader label="Software ID" tableType="systems" columnKey="software_id" currentConfig={sortConfig.systems} handleSort={handleSort} />
                    )}
                    {visibleColumns.systems.amc && (
                      <SortableHeader label="AMC Status" tableType="systems" columnKey="amc" currentConfig={sortConfig.systems} handleSort={handleSort} />
                    )}
                    {visibleColumns.systems.registered_at && (
                      <SortableHeader label="Registered At" tableType="systems" columnKey="registered_at" currentConfig={sortConfig.systems} handleSort={handleSort} />
                    )}
                    {visibleColumns.systems.actions && (
                      <TableCell align="center">Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedSystems.map((system) => (
                    <TableRow key={system.uuid} hover>
                      {visibleColumns.systems.uuid && (
                        <TableCell>
                          <Tooltip title={copiedField === `sys-${system.uuid}` ? "Copied!" : ""}>
                            <Box
                              onClick={() => handleCopy(system.uuid, `sys-${system.uuid}`)}
                              onMouseEnter={(e) => handleSystemHover(e, system.uuid)}
                              onMouseLeave={handleSystemHoverClose}
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                            >
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                                {system.uuid.substring(0, 8)}...
                              </Typography>
                              <ContentCopy sx={{ fontSize: 14, opacity: 0.5, color: copiedField === `sys-${system.uuid}` ? 'success.main' : 'inherit' }} />
                              {isRecentlyRegistered(system.registered_at) && (
                                <Chip size="small" label="New" sx={{ ...getStatusColorSx('new', darkMode), height: 18, fontSize: '0.65rem', ml: 0.5 }} />
                              )}
                            </Box>
                          </Tooltip>
                        </TableCell>
                      )}
                      {visibleColumns.systems.project_id && (
                        <TableCell>
                          <Chip
                            size="small"
                            label={system.project_id || 'N/A'}
                            sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', fontWeight: 600 }}
                          />
                        </TableCell>
                      )}
                      {visibleColumns.systems.software_id && (
                        <TableCell>
                          <Chip
                            size="small"
                            label={system.software_id || 'N/A'}
                            sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1), color: 'secondary.main', fontWeight: 600 }}
                          />
                        </TableCell>
                      )}
                      {visibleColumns.systems.amc && (
                        <TableCell>
                          <Chip
                            size="small"
                            label={system.amc ? "Active" : "Inactive"}
                            sx={{
                              bgcolor: system.amc ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.grey[500], 0.1),
                              color: system.amc ? 'success.main' : (darkMode ? '#93A2BC' : 'text.secondary'),
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                      )}
                      {visibleColumns.systems.registered_at && (
                        <TableCell>
                          <Typography variant="caption" sx={{ color: darkMode ? '#93A2BC' : 'text.secondary' }}>
                            {formatToIST(system.registered_at)}
                          </Typography>
                        </TableCell>
                      )}
                      {visibleColumns.systems.actions && (
                        <TableCell align="center">
                          <Tooltip title="Add License for this System">
                            <IconButton
                              color="success"
                              size="small"
                              sx={{ mr: 1 }}
                              onClick={async () => {
                                const systemOptions = await fetchSystemOptions();
                                setNewLicense({ uuid: system.uuid, start_date: '', end_date: '', systemOptions });
                                setSystemSearch(system.uuid);
                                setOpenDialog(prev => ({ ...prev, license: true }));
                              }}
                            >
                              <VpnKey fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit System">
                            <IconButton
                              color="primary"
                              onClick={() => handleEditSystem(system)}
                              size="small"
                              sx={{ mr: 1 }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete System">
                            <IconButton
                              color="error"
                              onClick={() => deleteSystem(system.uuid)}
                              size="small"
                            >
                              <Trash2 size={18} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {filteredSystems.length > rowsPerPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Pagination
                  count={Math.ceil(filteredSystems.length / rowsPerPage)}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size={isMobile ? 'medium' : 'large'}
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}

            {filteredSystems.length === 0 && (
              <EmptyState darkMode={darkMode} title="No systems found" subtitle="Try adjusting your search or add a new system" />
            )}
          </Card>
        )}
      </Box>
    </Fade>
  );
}

export default SystemsView;
